import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Container,
  Flex,
  FlexItem,
  Icon,
  Spinner,
  A,
  Badge,
  H2,
  H3,
  Text,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';

import { RootState } from '../../../../redux/reducers';

import { selectors, fetchMatchResume } from '../../state/reducer';

import ResumeModal from './components/ResumeModal';
import { ResumeData, MatchInfoSections } from '../../types';
import { DATE_FORMAT } from '../../../../constants';
import EmptyStateContainer from '../EmptyStateContainer';
import WorkDescription from './components/WorkDescription';

const { FormattedMessage, injectIntl } = Intl;

interface MatchResumeActions {
  fetchMatchResume: typeof fetchMatchResume;
}

export interface MatchResumeProps extends MatchResumeActions {
  isLoadingResume: boolean;
  resumeData?: ResumeData;
  infoSections?: MatchInfoSections;
}

const MatchResume = ({
  isLoadingResume,
  resumeData,
  fetchMatchResume,
  infoSections,
}: MatchResumeProps) => {
  const candidateId = infoSections?.general?.candidate?.id;
  const [resumeModalVisible, setResumeModalVisible] = useState(false);

  useEffect(() => {
    fetchMatchResume(Number(candidateId));
  }, [fetchMatchResume, candidateId]);

  const handleOpenResume = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault();
    setResumeModalVisible(true);
  };

  if (isLoadingResume) {
    return (
      <Container
        gutterDirection="vertical"
        background="pageSecondary"
        style={{ padding: '300px 0', textAlign: 'center' }}
      >
        <Spinner />
      </Container>
    );
  }

  if (!resumeData) {
    return null;
  }

  const { candidate, workHistory, educationHistory, resume } = resumeData;

  if (
    !candidate?.email &&
    !candidate?.phoneNumber &&
    !candidate?.linkedInProfileUrl &&
    !resume?.html &&
    !resume?.resume &&
    !educationHistory &&
    !workHistory
  ) {
    return (
      <EmptyStateContainer
        text={
          <FormattedMessage id={messageIds.vms.matchRecord.resume.emptyState} />
        }
      />
    );
  }

  return (
    <Container
      padding={12}
      gutter={4}
      gutterDirection="vertical"
      background="pageSecondary"
      stretch
    >
      {candidate && (
        <>
          <Flex gutter={4}>
            {candidate.email && (
              <FlexItem flex="none">
                <Icon type="messages" size="small" /> {candidate.email}
              </FlexItem>
            )}
            {candidate.email && candidate.phoneNumber && (
              <FlexItem flex="none">
                <Badge dot color="disabled" />
              </FlexItem>
            )}
            {candidate.phoneNumber && (
              <FlexItem flex="none">
                <Icon type="call" size="small" /> {candidate.phoneNumber}
              </FlexItem>
            )}
          </Flex>
          {candidate.linkedInProfileUrl && (
            <Flex gutter={4}>
              <FlexItem flex="none">
                <Icon type="linkedin" size="small" />{' '}
                <A
                  href={candidate.linkedInProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id={messageIds.vms.matchRecord.resume.linkedInLink}
                  />
                </A>
              </FlexItem>
            </Flex>
          )}
          {resume && (
            <Flex gutter={4}>
              <FlexItem flex="none">
                <A onClick={handleOpenResume}>
                  <Icon type="open" size="small" />{' '}
                  <FormattedMessage
                    id={messageIds.vms.matchRecord.resume.defaultResume}
                  />
                </A>
              </FlexItem>
            </Flex>
          )}
        </>
      )}

      <H2 color="headerPrimary">
        <FormattedMessage
          id={messageIds.vms.matchRecord.resume.educationHistory}
        />
      </H2>

      {educationHistory && educationHistory.length ? (
        educationHistory.map(education => (
          <Flex key={education.id}>
            <FlexItem>
              {education.schoolName && (
                <H3 color="headerPrimary">{education.schoolName}</H3>
              )}
              <Text>
                {education.educationLevel}
                {education.educationLevel && education.majors && ' | '}
                {education.majors}
              </Text>
              <p>
                {education.address && (
                  <Text>
                    {education.address.city}
                    {education.address.city &&
                      education.address.stateCode &&
                      ', '}
                    {education.address.stateCode}
                    {education.address &&
                    (education.address.city || education.address.stateCode)
                      ? ' | '
                      : null}
                    {education.startDate &&
                      moment(education.startDate).format('YYYY')}
                    {education.startDate &&
                    (education.endDate || education.graduationYear)
                      ? ' - '
                      : null}
                    {education.graduationYear}
                    {!education.graduationYear &&
                      education.endDate &&
                      moment(education.endDate).format('YYYY')}
                  </Text>
                )}
              </p>
            </FlexItem>
          </Flex>
        ))
      ) : (
        <H3>
          <FormattedMessage id={messageIds.vms.errors.noData} />
        </H3>
      )}

      <H2 color="headerPrimary">
        <FormattedMessage id={messageIds.vms.matchRecord.resume.workHistory} />
      </H2>

      {workHistory && workHistory.length ? (
        workHistory.map(work => (
          <Flex key={work.id}>
            <FlexItem>
              {work.externalCompanyName && (
                <H3 color="headerPrimary">{work.externalCompanyName}</H3>
              )}
              <p>
                <Text>
                  {work.startDate && moment(work.startDate).format(DATE_FORMAT)}{' '}
                  -{' '}
                  {work.endDate ? (
                    moment(work.endDate).format(DATE_FORMAT)
                  ) : (
                    <FormattedMessage
                      id={messageIds.vms.matchRecord.resume.present}
                    />
                  )}
                </Text>
              </p>
              {work.address && (
                <p>
                  <Text>
                    {work.address.city}
                    {work.address.city && work.address.stateCode
                      ? `, ${work.address.stateCode}`
                      : null}
                  </Text>
                </p>
              )}
              {work.description && (
                <WorkDescription description={work.description} />
              )}
            </FlexItem>
          </Flex>
        ))
      ) : (
        <H3>
          <FormattedMessage id={messageIds.vms.errors.noData} />
        </H3>
      )}

      {candidate && resume && (
        <ResumeModal
          visible={resumeModalVisible}
          name={candidate.name}
          resume={resume}
          onCancel={() => setResumeModalVisible(false)}
        />
      )}
    </Container>
  );
};

export const MatchResumeWithIntl = injectIntl(MatchResume);

export default connect(
  (state: RootState) => ({
    isLoadingResume: selectors.selectMatchRecord(state).isLoadingResume,
    resumeData: selectors.selectMatchRecord(state).resumeData,
    infoSections: selectors.selectMatchRecord(state).infoSections,
  }),
  { fetchMatchResume },
)(MatchResumeWithIntl);
